import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { GridCellProps } from '@progress/kendo-react-grid';
import { ChargeCalculation, ServiceLocationModel, ServiceModel } from '../../data/entities';
import { ServiceLocationService } from '../../services';
import { sharedProperty, useAsync, useCRUDHandlers, useGridActions, useSharedState, useTenantFeatures } from '../../hooks';
import { AddServiceLocationModal, EditServiceLocationModal } from './components';
import {
  Breakpoints,
  ConfirmationModal,
  Icon,
  IconDefinitions,
  ODataGrid,
  ResponsiveGridColumn } from '../../components';
import { useSelector } from 'react-redux';
import { getUserInfo, isSysAdmin, isTenantAdmin } from '../../app';
import { createODataQueryFilter, formatPrice } from '../../utils';

interface ServiceLocationsPageProps {
  service: ServiceModel;
  showState: sharedProperty<boolean>;
}

export const ServiceLocationsPage = forwardRef((props: Readonly<ServiceLocationsPageProps>, ref) => {

  const gridData = useSharedState<Array<ServiceLocationModel>>([]);
  const serviceLocationsService = new ServiceLocationService();

  const [refresh, setRefreshKey]= useState<boolean>(false);
  const { onClick, onSelect, showAddState, editItemState,  showEditState, showDeleteState, showDelete, deleteItemState } = useGridActions<ServiceLocationModel>()

  useImperativeHandle(ref, () => ({
    addLocation: () => {
      setRefreshKey(prev => !prev);
      showAddState.setter(true);
    }
  }));
  const { gridRef, getItems, addItem, editItem, deleteItem } = useCRUDHandlers<ServiceLocationModel>(serviceLocationsService, "Location", showAddState, showEditState);
  
  const tenantAdmin = useSelector(isTenantAdmin())
  const systemAdmin = useSelector(isSysAdmin())
  const { manageLocationServices } = useTenantFeatures();
  
  const userLocations = useSelector(getUserInfo())?.locationIds ?? [];   
  const locationQueryParam = useSelector(getUserInfo())?.locationIds?.join(",") ?? "";
  const {value: serviceLocations } = useAsync(async () => await serviceLocationsService.getByServiceAndLocationAsync(props.service.id, locationQueryParam),[refresh]);
  const serviceLocationIds = serviceLocations?.map((location) => location.locationId).filter((id): id is number => id !== null && id !== undefined) ?? [];
  const filteredLocations = userLocations.filter((location) => !serviceLocationIds.includes(location));


  const commandsCell = useCallback(
    (props: GridCellProps) => (
      <td className="text-center">
        <Icon
          iconName={IconDefinitions.delete}
          className="text-danger"
          onClick={() => showDelete(props.dataItem)}
        />
      </td>
    ),
    [showDelete]
  );

  const priceCell = useCallback(
    (gridProps: GridCellProps) => (
      <td>
        {gridProps.dataItem.price ? (
          <span className ="fw-semibold">
          {formatPrice(gridProps.dataItem.price)}
          </span>
        ) : (
          <span className="text-light-muted">
            {formatPrice(props.service.defaultPrice)}
          </span>
        )}
      </td>
    ),
    []
  );
  const chargeAmountCell = useCallback(
    (gridProps: GridCellProps) => (
      <td>
        {gridProps.dataItem.chargeAmount ? (
          gridProps.dataItem.chargeCalculation === ChargeCalculation.Fixed ? (
            <span className="fw-semibold">
              {formatPrice(gridProps.dataItem.chargeAmount)}
            </span>
          ) : (
            <span className="fw-semibold">
              {`${gridProps.dataItem.chargeAmount}%`}
            </span>
          )
        ) : (
          <span className="text-light-muted">
            {props.service.defaultChargeCalculation === ChargeCalculation.Fixed ? (
              formatPrice(props.service.defaultChargeAmount)
            ) : (
              `${props.service.defaultChargeAmount}%`
            )}
          </span>
        )}
      </td>
    ),
    []
  );
  const chargeTypeCell = useCallback(
    (gridProps: GridCellProps) => (
      <td>
        {gridProps.dataItem.chargeType ? (
          <span className="fw-semibold">
        {`${gridProps.dataItem.chargeType}`}
          </span>
        ) : (
          <span className="text-light-muted">
            {props.service.defaultChargeType}
          </span>
        )}
      </td>
    ),
    []
  );
  const chargeCalcCell = useCallback(
    (gridProps: GridCellProps) => (
      <td>
        {gridProps.dataItem.chargeCalculation ? (
          <span className="fw-semibold">
        {`${gridProps.dataItem.chargeCalculation}`}
          </span>
        ) : (
          <span className="text-light-muted">
        {props.service.defaultChargeCalculation}
          </span>
        )}
      </td>
    ),
    []
  );

  return (
    <Container>
      <Row className="g-0 p-3">
        <Col>
          <ODataGrid
            ref={gridRef}
            dataState={gridData}
            getData={(oDataQuery) =>{
              if(tenantAdmin || systemAdmin){
                const serviceFilter = [`serviceId eq ${props.service.id}`]
                const filterQuery = createODataQueryFilter(oDataQuery, serviceFilter)
                return getItems(`${filterQuery}&$expand=Location`)
              }
               const locationFilter = userLocations?.length ? `(${userLocations.map(location => `locationId eq ${location}`).join(" or ")})`: `locationId eq -1`; 
               const serviceFilter = [`serviceId eq ${props.service.id}`, locationFilter ]
               const filterQuery = createODataQueryFilter(oDataQuery, serviceFilter)
               return getItems(`${filterQuery}&$expand=Location`)
              }}
            onRowClick={manageLocationServices ? onClick : () => {return}}
            onRowMenuSelect={onSelect}
            sort={[{ field: "location.name", dir: "asc"}]}
            filterable={false}
          >
            <ResponsiveGridColumn
              title="Location"
              sortable={true}
              field="location.name"
            />
            <ResponsiveGridColumn
              title="Price"
              sortable={true}
              format='{0:c}'
              field="price"
              cell={priceCell}
              width={"auto"}
              breakpoint={Breakpoints.sm}
            />
             <ResponsiveGridColumn
              title="Charge Type"
              field="chargeType"
              sortable={true}
              cell={chargeTypeCell}
              width={"auto"}
              breakpoint={Breakpoints.md}
            />
            <ResponsiveGridColumn
              title="Calculation"
              sortable={true}
              field="chargeCalculation"
              cell ={chargeCalcCell}
              width={"auto"}
              breakpoint={Breakpoints.lg}
            />
            <ResponsiveGridColumn
              title="Amount"
              sortable={true}
              format='{0:c}'
              field="chargeAmount"
              cell={chargeAmountCell}
              width={"auto"} 
              breakpoint={Breakpoints.xl}
            />
            {manageLocationServices && (
              <ResponsiveGridColumn
                className="column-url"
                field="url"
                title=" "
                sortable={false}
                headerCell={() => null}
                cell={commandsCell}
                width={50}
              />
            )}
          </ODataGrid>
        </Col>
      </Row>
      <EditServiceLocationModal
        showState={showEditState}
        dataItem={editItemState.value}
        service={props.service}
        onSubmit={editItem}
      />
      <AddServiceLocationModal
        showState={showAddState}
        onSubmit={addItem}
        service={props.service}
        filteredLocations={filteredLocations}
      />
      <ConfirmationModal
        showState={showDeleteState}
        title={`Remove Service Location `}
        message={`You are about to remove the service from this location, it cannot be undone. Are you sure you want to remove ?`}
        confirmText="Delete"
        onConfirm={() => deleteItem(deleteItemState.value)}
      />
    </Container>
  )
});

export default ServiceLocationsPage;
